import { all, put, select, race, take, takeEvery } from 'redux-saga/effects'
import { SUCCESS, FAILED } from 'crom-api-constants'
import _ from 'lodash'

import { getToken, getLocale, getCustomData } from '@global/selectors'
import config from '@config'

import { blockSaveCustomData } from '../../../../../../../components/Workspace/components/HocBlock/actions'

import {
    COMPANY_AS_CREDITOR_LOAD_TABLE_DATA,
    COMPANY_AS_CREDITOR_TABLE_CHANGE,
    COMPANY_AS_CREDITOR_LOAD_TABLE_DATA_REQUEST,
    COMPANY_AS_CREDITOR_TABLE_LOADING_TRUE,
    COMPANY_AS_CREDITOR_TABLE_LOADING_FALSE,
    COMPANY_AS_CREDITOR_TABLE_DATA_RECEIVED,
} from './constants'

import {
    companyAsCreditorLoadTableDataRequest,
    companyAsCreditorTableLoadingTrue,
    companyAsCreditorTableLoadingFalse,
    companyAsCreditorTableDataReceived,
} from './actions'

const COMPANY_AS_CREDITOR_LOAD_TABLE_DATA_REQUEST_SUCCESS = (tableId) =>
    `${COMPANY_AS_CREDITOR_LOAD_TABLE_DATA_REQUEST(tableId)}_${SUCCESS}`
const COMPANY_AS_CREDITOR_LOAD_TABLE_DATA_REQUEST_FAILED = (tableId) =>
    `${COMPANY_AS_CREDITOR_LOAD_TABLE_DATA_REQUEST(tableId)}_${FAILED}`

function* companyAsCreditorTablesLoadCallSaga(action) {
    const block = _.get(action, 'data.block', null)
    if (block !== config.modules.report.blocks.companyAsCreditor.key) return null

    const entId = _.get(action, 'data.entId', null)
    const module = _.get(action, 'data.module', null)
    if (!module || !entId || !block) return null

    const tableId = _.get(action, 'data.tableId', null)
    const tableSelector = _.get(action, 'data.tableSelector', null)
    const level = _.get(action, 'data.level', null)
    const customData = _.get(action, 'data.customData', null)
    const locale = yield select(getLocale)
    const token = yield select(getToken, entId, module)

    const lang = config.languages[locale].requestKey

    yield put(
        companyAsCreditorTableLoadingTrue(module, block, entId, {
            [tableId]: {
                ..._.get(customData, `${tableId}`, {}),
                isLoading: true,
                dataLocale: null,
            },
        })
    )

    yield put(
        companyAsCreditorLoadTableDataRequest(
            tableId,
            tableSelector,
            entId,
            module,
            block,
            level,
            lang,
            token,
            _.get(config, `api.${module}.${block}.tables.${tableSelector}.qs`, {})
        )
    )

    const response = yield race({
        success: take(COMPANY_AS_CREDITOR_LOAD_TABLE_DATA_REQUEST_SUCCESS(tableId)),
        failed: take(COMPANY_AS_CREDITOR_LOAD_TABLE_DATA_REQUEST_FAILED(tableId)),
    })

    if (!response.failed) {
        const responseBody = _.get(response, 'success.payload.response.body', {})

        yield put(
            companyAsCreditorTableDataReceived(module, entId, block, {
                [tableId]: {
                    isLoading: false,
                    loaded: true,
                    failed: false,
                    dataLocale: locale,
                    data: _.get(responseBody, 'data', []),
                    pagination: _.get(responseBody, 'query.pagination', {}),
                    filter: _.get(responseBody, 'query.filter', {}),
                    sorted: _.reduce(
                        _.get(responseBody, 'query.sort.sortedColumns'),
                        (acc, val) => [
                            ...acc,
                            {
                                id: _.get(val, 'columm'), // TYPO FROM BACKEND
                                desc: _.get(val, 'direction') === 'desc',
                            },
                        ],
                        []
                    ),
                },
            })
        )
    } else {
        yield put(
            companyAsCreditorTableLoadingFalse(module, block, entId, {
                [tableId]: {
                    ..._.get(customData, `${tableId}`, {}),
                    isLoading: false,
                    loaded: true,
                    failed: true,
                },
            })
        )
    }

    return null
}

function* companyAsCreditorTableChangeCallSaga(action) {
    const block = _.get(action, 'data.block', null)
    if (block !== config.modules.report.blocks.companyAsCreditor.key) return null

    const entId = _.get(action, 'data.entId', null)
    const module = _.get(action, 'data.module', null)
    if (!module || !entId || !block) return null

    const tableId = _.get(action, 'data.tableId', null)
    const tableSelector = _.get(action, 'data.tableSelector', null)
    const level = _.get(action, 'data.level', null)
    const customData = _.get(action, 'data.customData', null)
    const queryParams = _.get(action, 'data.queryParams', null)
    const locale = yield select(getLocale)
    const token = yield select(getToken, entId, module)

    const lang = config.languages[locale].requestKey

    const filters = _.reduce(
        queryParams?.filtered,
        (acc, filter) => ({
            ...acc,
            [`${filter.id}[]`]: filter.value,
        }),
        {}
    )

    const qp = {
        'sort[]': _.map(_.get(queryParams, 'sorted', []), (val) => `${_.get(val, 'id')}${val.desc ? ':desc' : ':asc'}`),
        page: _.get(queryParams, 'pagination.page'),
        rows: _.get(queryParams, 'pagination.rowsPerPage'),
        ...filters,
    }

    yield put(
        companyAsCreditorTableLoadingTrue(module, block, entId, {
            [tableId]: {
                ..._.get(customData, `${tableId}`, {}),
                isLoading: true,
            },
        })
    )

    yield put(
        companyAsCreditorLoadTableDataRequest(tableId, tableSelector, entId, module, block, level, lang, token, qp)
    )

    const response = yield race({
        success: take(COMPANY_AS_CREDITOR_LOAD_TABLE_DATA_REQUEST_SUCCESS(tableId)),
        failed: take(COMPANY_AS_CREDITOR_LOAD_TABLE_DATA_REQUEST_FAILED(tableId)),
    })

    if (!response.failed) {
        const responseBody = _.get(response, 'success.payload.response.body', {})

        yield put(
            companyAsCreditorTableDataReceived(module, entId, block, {
                [tableId]: {
                    isLoading: false,
                    loaded: true,
                    dataLocale: locale,
                    data: _.get(responseBody, 'data', []),
                    pagination: _.get(responseBody, 'query.pagination', {}),
                    filter: _.get(responseBody, 'query.filter', {}),
                    sorted: _.reduce(
                        _.get(responseBody, 'query.sort.sortedColumns'),
                        (acc, val) => [
                            ...acc,
                            {
                                id: _.get(val, 'columm'), // TYPO FROM BACKEND
                                desc: _.get(val, 'direction') === 'desc',
                            },
                        ],
                        []
                    ),
                },
            })
        )

        return null
    }

    yield put(
        companyAsCreditorTableLoadingFalse(module, block, entId, {
            [tableId]: {
                ..._.get(customData, `${tableId}`, {}),
                isLoading: false,
                loaded: true,
            },
        })
    )

    return null
}

function* companyAsCreditorSaveCustomDataCallSaga(action) {
    const blockKey = _.get(action, 'data.block', null)
    if (blockKey !== config.modules.report.blocks.companyAsCreditor.key) return null

    const module = _.get(action, 'data.module', null)
    const id = _.get(action, 'data.id', null)
    if (!module || !id || !blockKey) return null

    const customData = yield select(getCustomData, module, blockKey, id)
    const incomingChange = _.get(action, 'data.customData', {})

    yield put(blockSaveCustomData(module, id, blockKey, { ...customData, ...incomingChange }))

    return null
}

export default function* companyAsCreditorSaga() {
    yield all([
        takeEvery(COMPANY_AS_CREDITOR_LOAD_TABLE_DATA, companyAsCreditorTablesLoadCallSaga),
        takeEvery(COMPANY_AS_CREDITOR_TABLE_LOADING_TRUE, companyAsCreditorSaveCustomDataCallSaga),
        takeEvery(COMPANY_AS_CREDITOR_TABLE_LOADING_FALSE, companyAsCreditorSaveCustomDataCallSaga),
        takeEvery(COMPANY_AS_CREDITOR_TABLE_DATA_RECEIVED, companyAsCreditorSaveCustomDataCallSaga),
        takeEvery(COMPANY_AS_CREDITOR_TABLE_CHANGE, companyAsCreditorTableChangeCallSaga),
    ])
}
